/**
 * Cuerpo — DESKTOP.
 *
 * Lo que el ancho permite y el celular no: las cuatro series (peso, grasa %, grasa kg, masa
 * magra) **en el mismo eje temporal y a la vista**, más la tabla completa de lecturas con sus
 * condiciones de medición. Ver peso bajando mientras la magra se mantiene plana es la lectura
 * que la app existe para dar, y necesita las curvas una al lado de la otra.
 *
 * El formulario es un panel fijo a la derecha, no una hoja: acá se carga con teclado.
 */

import { useCallback, useEffect, type ReactNode } from 'react';
import {
  AVISO_BIOIMPEDANCIA,
  AVISO_CONDICIONES,
  CONSEJO_MEDICION,
  MIN_LECTURAS_COMPARABLES,
  UMBRAL_PRATIO_BAJO,
  condicionesMixtas,
  formatearNumero,
  type Condiciones,
} from '@mg/core';

import { leyendaDeSerie, notaDePocasLecturas, useCuerpo } from '../features/useCuerpo.ts';
import { LineaConMedia } from '../ui/charts.tsx';
import { Aviso, Campo, Cifra, Insignia, Pila, Selector, Vacio, estadosDeRatio } from '../ui/componentes.tsx';
import { useRuta } from '../ui/router.tsx';

const CONDICIONES: { valor: Condiciones; etiqueta: string }[] = [
  { valor: 'ayuno', etiqueta: 'En ayunas' },
  { valor: 'post_entreno', etiqueta: 'Después de entrenar' },
  { valor: 'desconocido', etiqueta: 'Sin registrar' },
];

export function CuerpoDesktop(): ReactNode {
  const c = useCuerpo();
  const { ir, detalle } = useRuta();
  const { guardado, descartarGuardado } = c;
  const cargando = c.datos.cargando;

  /**
   * El panel de la derecha ya está a la vista: lo que falta es el cursor donde se escribe. El id
   * lo deriva <Campo> de la etiqueta ("Peso (kg)" → c-peso-kg), que es contrato de §12.
   */
  const enfocarPeso = useCallback(() => {
    const campo = document.getElementById('c-peso-kg');
    if (campo instanceof HTMLInputElement) campo.focus();
  }, []);

  // ir('cuerpo', 'medir') es la salida de la foto sin señal: se llega acá a escribir el peso.
  useEffect(() => {
    if (detalle === 'medir' && !cargando) enfocarPeso();
  }, [detalle, cargando, enfocarPeso]);

  // La confirmación se va sola: en el escritorio se encadena una medición tras otra y un cartel
  // que hay que cerrar a mano estorba más de lo que informa.
  useEffect(() => {
    if (!guardado) return;
    const reloj = setTimeout(descartarGuardado, 3000);
    return () => clearTimeout(reloj);
  }, [guardado, descartarGuardado]);

  if (cargando) return <Vacio>Cargando…</Vacio>;

  const mixtas = condicionesMixtas(c.datos.medidas);
  const notaMagra = notaDePocasLecturas(c.lecturasMagra);

  /** El pie de la tarjeta Velocidad: si no hay cifra, dice qué falta para tenerla. */
  const subtituloVelocidad =
    c.velocidad.kgPorSemana !== null
      ? `${formatearNumero(c.velocidad.kgPorSemana, 2)} kg/semana`
      : c.velocidad.estado !== 'insuficiente'
        ? c.velocidad.detalle
        : c.pratio.lecturas < MIN_LECTURAS_COMPARABLES
          ? `Necesita ${MIN_LECTURAS_COMPARABLES} lecturas en 4 semanas; hay ${c.pratio.lecturas}.`
          : 'Necesita lecturas de días distintos para estimarla.';

  return (
    <div className="d-rejilla" style={{ gridTemplateColumns: 'minmax(0, 1fr) 340px', alignItems: 'start' }}>
      <div className="columna" style={{ gap: 'var(--e5)' }}>
        <div className="d-rejilla d-rejilla--4">
          <div className="tarjeta">
            <span className="eyebrow">Calidad de la pérdida</span>
            {c.pratio.estado === 'ok' && c.pratio.calidad !== null ? (
              <>
                <Cifra valor={c.pratio.calidad} unidad="%" decimales={0} />
                <div style={{ marginTop: 8 }}>
                  <Pila
                    estados={estadosDeRatio(
                      c.pratio.calidad / 100,
                      10,
                      c.pratio.calidad >= UMBRAL_PRATIO_BAJO ? 'llena' : 'alerta',
                    )}
                    alto={12}
                  />
                </div>
              </>
            ) : (
              <>
                <Cifra valor={null} />
                <p className="nota--fina">{c.pratio.detalle}</p>
                {/* El conteo sin salida deja al usuario donde estaba. Sin racha ni reproche:
                    faltan lecturas, no disciplina. */}
                {c.pratio.estado === 'insuficiente' && (
                  <button
                    type="button"
                    className="boton boton--chico"
                    style={{ marginTop: 'var(--e2)' }}
                    onClick={enfocarPeso}
                  >
                    Medirme
                  </button>
                )}
              </>
            )}
          </div>

          <div className="tarjeta">
            <span className="eyebrow">Masa magra</span>
            <Cifra valor={c.derivadaUltima?.magraKg ?? null} unidad="kg" />
            {c.tendenciaMagra.enDescenso && <Insignia tono="alerta">bajando 3 lecturas</Insignia>}
            {/* La cifra sola se lee como un hecho; con una lectura detrás hay que decir de dónde
                sale. */}
            {notaMagra && <p className="nota--fina">{notaMagra}</p>}
          </div>

          {/* Sin altura, una rayita muda no dice que el dato que falta es tuyo. */}
          <div className="tarjeta">
            <span className="eyebrow">FFMI</span>
            {c.altura === null ? (
              <p className="nota" style={{ marginTop: 4 }}>
                Falta tu altura.
              </p>
            ) : (
              <>
                <Cifra valor={c.derivadaUltima?.ffmi ?? null} />
                <p className="nota--fina">{c.banda ? `${c.banda.etiqueta} · ${c.banda.rango}` : '—'}</p>
              </>
            )}
          </div>

          <div className="tarjeta">
            <span className="eyebrow">Velocidad</span>
            <div style={{ color: c.velocidad.excedeBanda ? 'var(--ambar)' : undefined }}>
              <Cifra valor={c.velocidad.porcentajeSemanal} unidad="%/sem" decimales={2} />
            </div>
            {/* Una raya bajo otra raya no dice nada. Cuando no hay número hay que decir qué falta,
                igual que hace FFMI acá al lado con la altura. */}
            <p className="nota--fina">{subtituloVelocidad}</p>
          </div>
        </div>

        <section className="tarjeta">
          <div className="fila fila--entre" style={{ marginBottom: 'var(--e4)' }}>
            <span className="eyebrow">Series · media móvil de 7 días sobre puntos crudos</span>
            <span className="nota--fina">{c.derivadas.length} lecturas</span>
          </div>

          <div className="d-rejilla d-rejilla--2">
            <div>
              <span className="nota--fina">Peso (kg)</span>
              <LineaConMedia puntos={c.composicion.peso} alto={170} unidad="kg" />
              <p className="nota--fina">{leyendaDeSerie(c.lecturasPeso)}</p>
            </div>
            <div>
              <span className="nota--fina">Masa magra (kg) — debería mantenerse plana</span>
              <LineaConMedia puntos={c.composicion.magraKg} alto={170} tono="agua" unidad="kg" />
            </div>
            <div>
              <span className="nota--fina">Grasa (%)</span>
              <LineaConMedia puntos={c.composicion.grasaPct} alto={170} tono="ambar" unidad="%" />
            </div>
            <div>
              <span className="nota--fina">Grasa (kg)</span>
              <LineaConMedia puntos={c.composicion.grasaKg} alto={170} tono="ambar" unidad="kg" />
            </div>
          </div>

          <p className="nota--fina" style={{ marginTop: 'var(--e4)' }}>
            {AVISO_BIOIMPEDANCIA}
          </p>
        </section>

        {mixtas && <Aviso tono="atencion" titulo="Condiciones mezcladas">{AVISO_CONDICIONES}</Aviso>}

        <section className="tarjeta" style={{ padding: 0 }}>
          <div style={{ padding: 'var(--e4)' }}>
            <span className="eyebrow">Todas las lecturas</span>
          </div>
          <div className="desborda-x">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th>Condiciones</th>
                  <th className="num">Peso</th>
                  <th className="num">Grasa %</th>
                  <th className="num">Grasa kg</th>
                  <th className="num">Magra</th>
                  <th className="num">Muscular</th>
                  <th className="num">FFMI</th>
                  <th className="num">Agua %</th>
                  <th className="num">Visceral</th>
                  <th className="num">TMB</th>
                  <th>Fuente</th>
                </tr>
              </thead>
              <tbody>
                {[...c.derivadas].reverse().map((d) => (
                  <tr key={d.medida.id}>
                    <td className="lectura">{d.fecha}</td>
                    <td className="lectura apagado">{d.medida.hora}</td>
                    <td>
                      {d.condiciones === 'desconocido' ? (
                        <span className="apagado">sin registrar</span>
                      ) : (
                        <Insignia>{d.condiciones === 'ayuno' ? 'ayuno' : 'post-entreno'}</Insignia>
                      )}
                    </td>
                    <td className="num">{formatearNumero(d.pesoKg)}</td>
                    <td className="num">{d.medida.grasa_pct === null ? '—' : formatearNumero(d.medida.grasa_pct)}</td>
                    <td className="num">{d.grasaKg === null ? '—' : formatearNumero(d.grasaKg)}</td>
                    <td className="num">{d.magraKg === null ? '—' : formatearNumero(d.magraKg)}</td>
                    <td className="num">
                      {d.medida.masa_muscular === null ? '—' : formatearNumero(d.medida.masa_muscular)}
                    </td>
                    <td className="num">{d.ffmi === null ? '—' : formatearNumero(d.ffmi)}</td>
                    <td className="num">{d.medida.agua_pct === null ? '—' : formatearNumero(d.medida.agua_pct)}</td>
                    <td className="num">
                      {d.medida.grasa_visceral === null ? '—' : formatearNumero(d.medida.grasa_visceral, 0)}
                    </td>
                    <td className="num">{d.medida.tmb === null ? '—' : formatearNumero(d.medida.tmb, 0)}</td>
                    <td>{d.medida.fuente === 'ocr' ? <Insignia tono="ambar">foto</Insignia> : 'a mano'}</td>
                  </tr>
                ))}
                {c.derivadas.length === 0 && (
                  <tr>
                    <td colSpan={13}>
                      <Vacio>Todavía no hay mediciones. Cargá una a la derecha o saca una foto de la balanza.</Vacio>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <aside className="columna" style={{ gap: 'var(--e4)' }}>
        <button type="button" className="boton boton--principal" onClick={() => ir('ingesta')}>
          Cargar desde una foto
        </button>

        {/* El consejo va ANTES de pesarse: al pie de la columna se leía cuando ya te bajaste de
            la balanza. */}
        <p className="nota--fina">{CONSEJO_MEDICION}</p>

        <section className="tarjeta">
          <span className="eyebrow">Medición a mano</span>
          <div className="columna" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
            <FormularioCompacto c={c} />
          </div>
        </section>

        {c.altura === null && (
          <Aviso tono="atencion" titulo="Falta tu altura">
            <p>
              Sin altura no hay FFMI ni FMI.{' '}
              {c.alturaSugerida ? `De tu IMC sale ${c.alturaSugerida} cm; confirmalo.` : 'Se pide una sola vez.'}
            </p>
            <div style={{ marginTop: 8 }}>
              <button type="button" className="boton boton--chico" onClick={() => ir('ajustes')}>
                Completarla
              </button>
            </div>
          </Aviso>
        )}
      </aside>
    </div>
  );
}

function FormularioCompacto({ c }: { c: ReturnType<typeof useCuerpo> }): ReactNode {
  const b = c.borrador;
  const set = (k: keyof typeof b, v: string) => c.setBorrador({ ...b, [k]: v });
  const errores = c.validacion.hallazgos.filter((h) => h.severidad === 'error');
  const confirmar = c.validacion.hallazgos.filter((h) => h.severidad === 'confirmar');

  const campos: [keyof typeof b, string][] = [
    ['peso', 'Peso (kg)'],
    ['grasa_pct', 'Grasa %'],
    ['masa_magra', 'Masa magra'],
    ['masa_muscular', 'Muscular'],
    ['masa_osea', 'Ósea'],
    ['agua_pct', 'Agua %'],
    ['grasa_visceral', 'Visceral'],
    ['imc', 'IMC'],
    ['tmb', 'TMB'],
  ];

  return (
    <>
      <div className="fila" style={{ gap: 'var(--e2)' }}>
        <div className="crece">
          <Campo etiqueta="Fecha" texto type="date" value={b.fecha} onChange={(e) => set('fecha', e.target.value)} />
        </div>
        <div style={{ width: 100 }}>
          <Campo etiqueta="Hora" texto type="time" value={b.hora} onChange={(e) => set('hora', e.target.value)} />
        </div>
      </div>

      <div className="d-rejilla d-rejilla--2" style={{ gap: 'var(--e2)' }}>
        {campos.map(([clave, etiqueta]) => (
          <Campo
            key={clave}
            etiqueta={etiqueta}
            inputMode="decimal"
            value={b[clave] as string}
            onChange={(e) => set(clave, e.target.value)}
          />
        ))}
      </div>

      <Selector
        etiqueta="Condiciones"
        value={b.condiciones}
        onChange={(e) => c.setBorrador({ ...b, condiciones: e.target.value as Condiciones })}
      >
        {CONDICIONES.map((x) => (
          <option key={x.valor} value={x.valor}>
            {x.etiqueta}
          </option>
        ))}
      </Selector>
      <p className="nota--fina">
        Ayuno o post-entreno: sin esto la lectura no se puede comparar con las demás.
      </p>

      {errores.length > 0 && <Aviso tono="importante">{errores.map((h) => h.mensaje).join(' ')}</Aviso>}

      {errores.length === 0 && confirmar.length > 0 && (
        <Aviso tono="atencion">
          {confirmar.map((h) => h.mensaje).join(' ')}
          <label className="fila" style={{ marginTop: 8, gap: 8 }}>
            <input type="checkbox" checked={c.confirmado} onChange={(e) => c.setConfirmado(e.target.checked)} />
            <span>Es correcto</span>
          </label>
        </Aviso>
      )}

      {/* Guardar tiene que confirmar o explicar; lo que no puede es quedarse callado. */}
      {c.guardado && (
        <Aviso tono="info">
          <strong>Guardado</strong> ·{' '}
          <span className="lectura">
            {formatearNumero(c.guardado.peso)} {c.guardado.unidad}
          </span>
        </Aviso>
      )}

      {c.error && <Aviso tono="importante">{c.error}</Aviso>}

      <button
        type="button"
        className="boton boton--principal"
        disabled={!c.puedeGuardar || c.guardando}
        onClick={() => void c.guardar()}
      >
        {c.guardando ? 'Guardando…' : 'Guardar medición'}
      </button>

      {!c.guardado && c.queFalta && <p className="nota--fina">{c.queFalta}</p>}
    </>
  );
}
